import { Controller } from "@hotwired/stimulus";

const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

// Libellés des champs qu'Argos peut refuser (réponse `invalid`, clé `fields`).
const FIELD_LABELS = {
  project_type: "le type de projet",
  budget: "le budget",
  name: "votre nom",
  company: "l'entreprise",
  email: "l'email",
  phone: "le téléphone",
  message: "le message (3 000 caractères au plus)",
};

// Formulaire de devis en trois étapes, envoyé à Argos (POST /quote-requests).
// Argos attend du JSON envoyé en text/plain (requête CORS simple), un jeton
// Turnstile et le champ piège `website` vide. Sans clé Turnstile configurée, la
// dernière étape prépare un email à la place.
export default class extends Controller {
  static targets = ["step", "label", "progress", "progressWrap", "back", "next", "nextLabel", "done", "sent", "actions", "turnstile", "error"];
  static values = { email: String, endpoint: String, siteKey: String };

  connect() {
    // `required` sert la version sans JavaScript, où tout s'affiche d'un coup.
    // Ici les étapes masquées bloqueraient l'envoi : le contrôleur valide lui-même.
    this.element.noValidate = true;
    this.current = 0;
    this.token = null;
    this.sending = false;
    this.progressWrapTarget.hidden = false;
    this.render();
  }

  disconnect() {
    if (this.widgetId !== undefined) window.turnstile?.remove(this.widgetId);
  }

  get live() {
    return this.siteKeyValue !== "" && this.endpointValue !== "";
  }

  get lastStep() {
    return this.stepTargets.length - 1;
  }

  back() {
    this.current = Math.max(0, this.current - 1);
    this.clearError();
    this.render();
    this.focusStep();
  }

  refresh() {
    this.nextTarget.disabled = this.sending || !this.isComplete();
  }

  submit(event) {
    event.preventDefault();
    if (this.sending || !this.isComplete()) return;

    if (this.current < this.lastStep) {
      this.current += 1;
      this.render();
      this.focusStep();
      return;
    }

    if (this.live) {
      this.send();
    } else {
      window.location.href = this.mailtoUrl();
      this.finish(this.doneTarget, "Prête à envoyer");
    }
  }

  render() {
    this.stepTargets.forEach((step, index) => { step.hidden = index !== this.current; });
    this.backTarget.hidden = this.current === 0;
    this.progressTarget.style.width = `${Math.round(((this.current + 1) / this.stepTargets.length) * 100)}%`;
    this.labelTarget.textContent = `Étape ${this.current + 1} sur ${this.stepTargets.length}`;
    this.nextLabelTarget.textContent = this.current === this.lastStep ? "Recevoir mon devis" : "Continuer";
    if (this.current === this.lastStep && this.live) this.showTurnstile();
    this.refresh();
  }

  focusStep() {
    this.stepTargets[this.current].querySelector("input, textarea")?.focus({ preventScroll: true });
  }

  isComplete() {
    const step = this.stepTargets[this.current];
    if (this.current < this.lastStep) return !!step.querySelector("input:checked");

    const emailOk = /.+@.+\..+/.test(this.value("email"));
    const phoneOk = this.value("phone").replace(/\D/g, "").length >= 6;
    const contactOk = this.value("name") !== "" && (emailOk || phoneOk);
    return contactOk && (!this.live || !!this.token);
  }

  value(name) {
    return (this.element.elements[name].value || "").trim();
  }

  label(name) {
    return this.element.querySelector(`input[name="${name}"]:checked`)?.dataset.label || "";
  }

  // Turnstile

  showTurnstile() {
    if (this.widgetId !== undefined || this.loadingTurnstile) return;
    this.loadingTurnstile = true;
    this.loadTurnstile()
      .then(() => {
        this.widgetId = window.turnstile.render(this.turnstileTarget, {
          sitekey: this.siteKeyValue,
          language: "fr",
          size: "flexible",
          callback: (token) => { this.token = token; this.refresh(); },
          "expired-callback": () => { this.token = null; this.refresh(); },
          "error-callback": () => {
            this.token = null;
            this.refresh();
            this.showError("La vérification anti-robot ne se charge pas. Rechargez la page, ou envoyez votre demande par email.", true);
          },
        });
      })
      .catch(() => this.showError("La vérification anti-robot ne se charge pas. Rechargez la page, ou envoyez votre demande par email.", true))
      .finally(() => { this.loadingTurnstile = false; });
  }

  loadTurnstile() {
    if (window.turnstile) return Promise.resolve();
    window.quoteTurnstile ||= new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = TURNSTILE_SCRIPT;
      script.async = true;
      script.onload = resolve;
      script.onerror = () => { window.quoteTurnstile = null; reject(new Error("turnstile")); };
      document.head.appendChild(script);
    });
    return window.quoteTurnstile;
  }

  resetTurnstile() {
    this.token = null;
    if (this.widgetId !== undefined) window.turnstile.reset(this.widgetId);
    this.refresh();
  }

  // Envoi à Argos

  async send() {
    this.sending = true;
    this.clearError();
    this.nextLabelTarget.textContent = "Envoi en cours…";
    this.element.setAttribute("aria-busy", "true");
    this.refresh();

    let response;
    let body = {};
    try {
      response = await fetch(this.endpointValue, {
        method: "POST",
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify(this.payload()),
      });
      body = await response.json().catch(() => ({}));
    } catch {
      response = null;
    } finally {
      this.sending = false;
      this.element.removeAttribute("aria-busy");
      this.nextLabelTarget.textContent = "Recevoir mon devis";
    }

    this.handle(response, body);
  }

  payload() {
    return {
      project_type: this.value("project_type"),
      budget: this.value("budget"),
      name: this.value("name"),
      company: this.value("company"),
      email: this.value("email"),
      phone: this.value("phone"),
      message: this.value("message"),
      page: window.location.pathname,
      website: this.element.elements.website.value,
      turnstile_token: this.token,
    };
  }

  handle(response, body) {
    if (response === null) {
      // La demande a pu partir : Argos reconnaît un second envoi des mêmes
      // réponses avant de redemander Turnstile, le jeton reste donc utilisable.
      this.showError("Votre demande n'est pas partie, la connexion a été interrompue. Réessayez dans un instant.", true);
      this.refresh();
      return;
    }

    if (response.ok && body.status === "received") {
      this.sentDetail();
      this.finish(this.sentTarget, "Envoyée");
      window.sa_event?.("quote_request_sent");
      return;
    }

    switch (body.error) {
      case "invalid":
        // Argos valide avant de consulter Turnstile : le jeton n'est pas dépensé.
        this.showInvalid(body.fields || []);
        break;
      case "turnstile":
        this.resetTurnstile();
        this.showError("La vérification anti-robot a expiré. Validez-la de nouveau, puis renvoyez votre demande.");
        break;
      case "rate_limit":
        this.showError("Plusieurs demandes sont déjà parties de votre connexion. Réessayez dans quelques minutes.", true);
        break;
      case "unavailable":
        this.resetTurnstile();
        this.showError("Le service est momentanément indisponible. Réessayez dans un instant.", true);
        break;
      default:
        this.resetTurnstile();
        this.showError("Votre demande n'a pas pu être envoyée.", true);
    }
    this.refresh();
  }

  showInvalid(fields) {
    const firstStep = fields.includes("project_type") ? 0 : fields.includes("budget") ? 1 : null;
    if (firstStep !== null) {
      this.current = firstStep;
      this.render();
    }
    const labels = fields.map((field) => FIELD_LABELS[field]).filter(Boolean);
    this.showError(labels.length ? `Vérifiez ${labels.join(", ")}.` : "Vérifiez vos réponses.");
  }

  sentDetail() {
    const paragraph = this.sentTarget.querySelector("p");
    paragraph.textContent = this.value("email")
      ? "Un email de confirmation part tout de suite à l'adresse indiquée. Je vous réponds sous 24 h avec un devis détaillé."
      : "Je vous rappelle sous 24 h au numéro indiqué pour préparer votre devis.";
  }

  finish(panel, label) {
    this.stepTargets.forEach((step) => { step.hidden = true; });
    this.actionsTarget.hidden = true;
    this.clearError();
    panel.hidden = false;
    // Plus rien à remplir : la barre de progression n'a plus de sens.
    this.progressWrapTarget.hidden = true;
    this.labelTarget.textContent = label;
    panel.focus();
  }

  // Erreurs

  showError(message, withEmail = false) {
    this.errorTarget.replaceChildren(document.createTextNode(message));
    if (withEmail) {
      const link = document.createElement("a");
      link.href = this.mailtoUrl();
      link.textContent = "Envoyer ma demande par email";
      this.errorTarget.append(" ", link);
    }
    this.errorTarget.hidden = false;
  }

  clearError() {
    this.errorTarget.hidden = true;
    this.errorTarget.replaceChildren();
  }

  mailtoUrl() {
    const lines = [
      `Projet : ${this.label("project_type")}`,
      `Budget : ${this.label("budget")}`,
      `Nom : ${this.value("name")}`,
      `Entreprise : ${this.value("company") || "non précisée"}`,
      `Email : ${this.value("email") || "non précisé"}`,
      `Téléphone : ${this.value("phone") || "non précisé"}`,
      "",
      this.value("message"),
    ];
    const subject = `Demande de devis : ${this.label("project_type")}`;
    return `mailto:${this.emailValue}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
  }
}
